import { useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, XCircle, Sparkles, Globe, ExternalLink } from "lucide-react";
import { CONTACT } from "@/config/contact";
import { buildCarSeo } from "@/lib/car-seo";

/**
 * Per-car SEO editor with live preview + length validation.
 *
 * Surfaces the same constraints Google and the build-time SEO test enforce
 * (title 30–60 chars, description 50–160 chars, slug ASCII-kebab) so admins
 * see warnings inline instead of discovering them only after publishing.
 *
 * Hard errors (oversize fields, invalid slug characters) are returned via
 * `onValidationChange` so the parent dialog can disable the save button —
 * we never block the slug field itself, since admins can still fix it.
 */

const TITLE_MIN = 30;
const TITLE_MAX = 60;
const DESC_MIN = 50;
const DESC_MAX = 160;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const SITE = CONTACT.websiteUrl.replace(/\/$/, "");

export interface CarSeoEditorValue {
  brand: string;
  model: string;
  variant: string;
  year: number;
  mileage: number;
  price: number;
  fuel_type: string;
  transmission: string;
  horsepower: number;
  description: string;
  is_leasing: boolean;
  leasing_price: number;
  seo_title: string;
  seo_description: string;
  seo_slug: string;
  images: string[];
}

export interface SeoValidationResult {
  /** Hard errors block the save button. */
  hasErrors: boolean;
  /** Soft warnings show in UI but don't block. */
  hasWarnings: boolean;
}

type Props = {
  value: CarSeoEditorValue;
  onChange: (next: { seo_title: string; seo_description: string; seo_slug: string }) => void;
  onValidationChange?: (result: SeoValidationResult) => void;
};

/** Convert a free-text input into a URL-safe ASCII kebab-case slug. */
function normalizeSlug(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[æ]/g, "ae")
    .replace(/[ø]/g, "oe")
    .replace(/[å]/g, "aa")
    .replace(/[^a-z0-9\s-]/g, " ")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

interface FieldState {
  level: "ok" | "warn" | "error";
  message: string;
}

function checkTitle(t: string): FieldState {
  const len = t.trim().length;
  if (len === 0) return { level: "warn", message: "Tom — Google bygger title fra side-data (mindre kontrol)" };
  if (len > TITLE_MAX) return { level: "error", message: `${len} tegn — Google trunkerer over ${TITLE_MAX}` };
  if (len < TITLE_MIN) return { level: "warn", message: `${len} tegn — kort, sigt efter ${TITLE_MIN}–${TITLE_MAX}` };
  return { level: "ok", message: `${len}/${TITLE_MAX} tegn` };
}

function checkDescription(d: string): FieldState {
  const len = d.trim().length;
  if (len === 0) return { level: "warn", message: "Tom — beskrivelsen bygges fra bilens specs (mindre kontrol)" };
  if (len > DESC_MAX) return { level: "error", message: `${len} tegn — Google trunkerer over ${DESC_MAX}` };
  if (len < DESC_MIN) return { level: "warn", message: `${len} tegn — kort, sigt efter ${DESC_MIN}–${DESC_MAX}` };
  return { level: "ok", message: `${len}/${DESC_MAX} tegn` };
}

function checkSlug(s: string): FieldState {
  const trimmed = s.trim();
  if (trimmed.length === 0) return { level: "warn", message: "Tom — auto-genereres ved gem (svært at huske)" };
  if (!SLUG_RE.test(trimmed)) {
    return {
      level: "error",
      message: "Kun små bogstaver a-z, tal og bindestreger. Brug 'Normaliser' for at rette.",
    };
  }
  if (trimmed.length > 80) return { level: "warn", message: `${trimmed.length} tegn — meget langt, fjern variant-detaljer` };
  return { level: "ok", message: `${trimmed.length} tegn` };
}

function StatusIcon({ level }: { level: FieldState["level"] }) {
  if (level === "ok") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />;
  if (level === "warn") return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
  return <XCircle className="h-3.5 w-3.5 text-destructive" />;
}

export function CarSeoEditor({ value, onChange, onValidationChange }: Props) {
  const titleState = useMemo(() => checkTitle(value.seo_title), [value.seo_title]);
  const descState = useMemo(() => checkDescription(value.seo_description), [value.seo_description]);
  const slugState = useMemo(() => checkSlug(value.seo_slug), [value.seo_slug]);

  // Surface validation result to the parent after render so it can disable Save
  // on hard errors without scheduling a parent state update during render.
  const hasErrors =
    titleState.level === "error" ||
    descState.level === "error" ||
    slugState.level === "error";
  const hasWarnings =
    titleState.level === "warn" || descState.level === "warn" || slugState.level === "warn";
  useEffect(() => {
    onValidationChange?.({ hasErrors, hasWarnings });
  }, [hasErrors, hasWarnings, onValidationChange]);

  const effectiveSlug = (value.seo_slug || "").trim() || "(auto-genereres ved gem)";
  const canonical = `${SITE}/biler-til-salg/${effectiveSlug === "(auto-genereres ved gem)" ? "<slug>" : effectiveSlug}`;

  // Live previews fall back to the same auto-generation logic the public
  // pages use, so admins see exactly what scrapers will see on save.
  const autoFallback = useMemo(() => {
    if (!value.brand || !value.model) {
      return { seo_title: "", seo_description: "", seo_slug: "" };
    }
    return buildCarSeo({
      brand: value.brand,
      model: value.model,
      variant: value.variant || null,
      year: value.year,
      mileage: value.mileage,
      price: value.price,
      fuel_type: value.fuel_type,
      transmission: value.transmission,
      horsepower: value.horsepower || null,
      description: value.description,
      is_leasing: value.is_leasing,
      leasing_price: value.leasing_price || null,
    });
  }, [
    value.brand,
    value.model,
    value.variant,
    value.year,
    value.mileage,
    value.price,
    value.fuel_type,
    value.transmission,
    value.horsepower,
    value.description,
    value.is_leasing,
    value.leasing_price,
  ]);

  const previewTitle = value.seo_title.trim() || autoFallback.seo_title;
  const previewDesc = value.seo_description.trim() || autoFallback.seo_description;
  const previewImage = value.images[0] || `${SITE}/og-default.jpg`;

  const fillFromAuto = () => {
    onChange({
      seo_title: autoFallback.seo_title,
      seo_description: autoFallback.seo_description,
      seo_slug: autoFallback.seo_slug,
    });
  };

  const normalizeSlugClick = () => {
    onChange({
      seo_title: value.seo_title,
      seo_description: value.seo_description,
      seo_slug: normalizeSlug(value.seo_slug),
    });
  };

  return (
    <div className="space-y-5">
      {/* Header with auto-fill */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">SEO &amp; canonical</h3>
          <p className="text-xs text-muted-foreground">
            Kontroller hvordan bilen vises i Google &amp; sociale medier før du publicerer.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={fillFromAuto}
          disabled={!autoFallback.seo_title}
        >
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          Foreslå fra bil-data
        </Button>
      </div>

      {/* Title */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-foreground">SEO Titel</label>
          <span className="flex items-center gap-1.5 text-[11px]">
            <StatusIcon level={titleState.level} />
            <span
              className={
                titleState.level === "error"
                  ? "text-destructive"
                  : titleState.level === "warn"
                    ? "text-amber-600 dark:text-amber-500"
                    : "text-muted-foreground"
              }
            >
              {titleState.message}
            </span>
          </span>
        </div>
        <Input
          value={value.seo_title}
          onChange={(e) => onChange({ ...value, seo_title: e.target.value })}
          maxLength={120}
          placeholder={autoFallback.seo_title || "F.eks. BMW X5 3.0 d 2021 – 549.000 kr | AutoXChange"}
          aria-invalid={titleState.level === "error"}
          className={titleState.level === "error" ? "border-destructive" : undefined}
        />
      </div>

      {/* Description */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-foreground">SEO Beskrivelse</label>
          <span className="flex items-center gap-1.5 text-[11px]">
            <StatusIcon level={descState.level} />
            <span
              className={
                descState.level === "error"
                  ? "text-destructive"
                  : descState.level === "warn"
                    ? "text-amber-600 dark:text-amber-500"
                    : "text-muted-foreground"
              }
            >
              {descState.message}
            </span>
          </span>
        </div>
        <Textarea
          value={value.seo_description}
          onChange={(e) => onChange({ ...value, seo_description: e.target.value })}
          maxLength={300}
          rows={3}
          placeholder={autoFallback.seo_description || "Kort beskrivelse vist i Google-resultater (50–160 tegn)"}
          aria-invalid={descState.level === "error"}
          className={descState.level === "error" ? "border-destructive" : undefined}
        />
      </div>

      {/* Slug + canonical */}
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-foreground">URL-slug</label>
          <span className="flex items-center gap-1.5 text-[11px]">
            <StatusIcon level={slugState.level} />
            <span
              className={
                slugState.level === "error"
                  ? "text-destructive"
                  : slugState.level === "warn"
                    ? "text-amber-600 dark:text-amber-500"
                    : "text-muted-foreground"
              }
            >
              {slugState.message}
            </span>
          </span>
        </div>
        <div className="flex gap-2">
          <Input
            value={value.seo_slug}
            onChange={(e) => onChange({ ...value, seo_slug: e.target.value })}
            placeholder={autoFallback.seo_slug || "bmw-x5-3-0-d-xdrive-2021-85000km"}
            aria-invalid={slugState.level === "error"}
            className={slugState.level === "error" ? "border-destructive font-mono text-sm" : "font-mono text-sm"}
          />
          {slugState.level === "error" && (
            <Button type="button" size="sm" variant="outline" onClick={normalizeSlugClick}>
              Normaliser
            </Button>
          )}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 rounded border border-dashed border-border bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground">
          <Globe className="h-3 w-3 flex-shrink-0" />
          <span className="font-medium text-foreground">Canonical:</span>
          <span className="truncate font-mono">{canonical}</span>
        </div>
      </div>

      {/* SERP preview */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Google forhåndsvisning
          </span>
          {previewTitle && (value.seo_slug || autoFallback.seo_slug) && (
            <a
              href={canonical.includes("<slug>") ? "#" : canonical}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (canonical.includes("<slug>")) e.preventDefault();
              }}
              className="inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
            >
              Åbn live <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <div className="rounded-lg border border-border bg-card px-4 py-3">
          <div className="text-[12px] text-muted-foreground">
            {SITE.replace(/^https?:\/\//, "")} ›{" "}
            <span>biler-til-salg › {(value.seo_slug || autoFallback.seo_slug || "<slug>").slice(0, 40)}</span>
          </div>
          <h4 className="mt-0.5 truncate text-[18px] leading-snug text-[#1a0dab] dark:text-[#8ab4f8]">
            {previewTitle || "(SEO Titel — udfyld feltet ovenfor)"}
          </h4>
          <p className="mt-1 line-clamp-2 text-[13px] leading-snug text-muted-foreground">
            {previewDesc || "(SEO Beskrivelse — udfyld feltet ovenfor)"}
          </p>
        </div>
      </div>

      {/* Social/OG preview */}
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Social forhåndsvisning (Facebook / LinkedIn / X)
        </div>
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="aspect-[1.91/1] w-full bg-muted">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={previewImage}
              alt={previewTitle}
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
          <div className="px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {SITE.replace(/^https?:\/\//, "")}
            </div>
            <div className="mt-0.5 truncate text-sm font-semibold text-foreground">
              {previewTitle || "(SEO Titel — udfyld feltet ovenfor)"}
            </div>
            <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {previewDesc || "(SEO Beskrivelse — udfyld feltet ovenfor)"}
            </div>
          </div>
        </div>
      </div>

      {hasErrors && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <XCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <span>
            Ret de markerede SEO-fejl før du gemmer — bilen kan ellers ikke publiceres med sikre meta-tags.
          </span>
        </div>
      )}
    </div>
  );
}
